/**
 * Spray benchmark level — which spray sends count toward a climber's level.
 *
 * Only BENCHMARK problems count: anybody can create a problem and grade it, so
 * an unchecked self-made "8a" would lift a climber's pyramid and climbing level
 * at will. A benchmark is set by a moderator and only on a public problem.
 * The rule reads the CURRENT problem state (benchmark flag, visibility, hide,
 * grade): the triggers re-sort past sends when it changes, nothing is frozen.
 * Spray never adds points — this module only feeds the level statistics.
 *
 * Spec: docs/spec/02d-shared-spraywall.md ("Spray benchmark level (sprayLevel.js)").
 */
import { consensusGrade } from "./sprayWall.js";
import { gradeBands, bandOfGrade } from "./gradeBands.js";
import { GRADE_SCALE } from "./gradeScale.js";

const isMap = (v) => Object.prototype.toString.call(v) === "[object Object]";
const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
const positiveCount = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** True when a problem's sends count toward the level right now. */
export function isBenchmarkEligible(problem) {
  if (!isMap(problem)) return false;
  return problem.benchmark === true && problem.visibility === "public" && problem.status !== "hidden";
}

/**
 * A problem's grade rung as shown everywhere: the community consensus, else
 * the author's grade. One rule for the problem list, the problem view and the level.
 */
export function problemGrade(problem) {
  if (!isMap(problem)) return null;
  const consensus = consensusGrade(problem.gradeVotes);
  if (Number.isInteger(consensus)) return consensus;
  return Number.isInteger(problem.grade) ? problem.grade : null;
}

/**
 * The `gymStats.sprayBenchmarkDays` map of one climber:
 * `{ [firstSendDay]: { g: { [rung]: sends }, fg: { [rung]: flashes } } }`.
 * Built from the climber's `sprayAscents` and the gym's problems by id.
 */
export function buildSprayBenchmarkDays(sentAscents, problemsById) {
  const days = {};
  const problems = isMap(problemsById) ? problemsById : {};
  for (const a of Array.isArray(sentAscents) ? sentAscents : []) {
    if (!isMap(a) || a.status !== "sent") continue;
    if (typeof a.date !== "string" || !DAY_KEY_RE.test(a.date)) continue;
    const problem = typeof a.problemId === "string" ? problems[a.problemId] : null;
    if (!isBenchmarkEligible(problem)) continue;
    const rung = problemGrade(problem);
    if (!Number.isInteger(rung)) continue;
    const key = String(rung);
    const day = days[a.date] || (days[a.date] = { g: {}, fg: {} });
    day.g[key] = (day.g[key] || 0) + 1;
    if (a.flash === true) day.fg[key] = (day.fg[key] || 0) + 1;
  }
  return days;
}

/** The benchmark sends of the days in `[from, to]` (inclusive, `null` = open) as `{ g, fg }`. */
export function benchmarkSendsInRange(days, from = null, to = null) {
  const out = { g: {}, fg: {} };
  if (!isMap(days)) return out;
  for (const [day, stat] of Object.entries(days)) {
    if ((from != null && day < from) || (to != null && day > to) || !isMap(stat)) continue;
    for (const field of ["g", "fg"]) {
      if (!isMap(stat[field])) continue;
      for (const [rung, n] of Object.entries(stat[field])) {
        const c = positiveCount(n);
        if (c > 0) out[field][rung] = (out[field][rung] || 0) + c;
      }
    }
  }
  return out;
}

/**
 * The level entries of the benchmark sends, one per grade rung:
 * `{ gradeIdx, count, flashes, key, points, np }`.
 * `days` is a `sprayBenchmarkDays` map or an already summed `{ g, fg }`.
 *  - gym scope (`difficultyConfig` given): the rung goes to the gym's
 *    difficulty level (`key`, `points`); a rung in no level is dropped, so the
 *    entry joins the same buckets as the route sends;
 *  - cross-gym (no `difficultyConfig`): `np` is the rung's normalized points.
 */
export function levelEntriesFromSpray(days, { difficultyConfig = null, gradeScale = GRADE_SCALE } = {}) {
  // A summed `{ g, fg }` has only those two keys; a days map has date keys.
  const summed = isMap(days) && isMap(days.g) && Object.keys(days).every((k) => k === "g" || k === "fg")
    ? days
    : benchmarkSendsInRange(days);
  const bands = difficultyConfig ? gradeBands(difficultyConfig).filter((b) => !b.fallback) : null;
  const out = [];
  for (const [rungKey, n] of Object.entries(summed.g || {})) {
    const c = positiveCount(n);
    const gradeIdx = Number(rungKey);
    if (c <= 0 || !Number.isInteger(gradeIdx)) continue;
    const flashes = Math.min(c, positiveCount(summed.fg?.[rungKey]));
    if (bands) {
      const band = bandOfGrade(bands, gradeIdx);
      if (!band) continue;
      out.push({ gradeIdx, count: c, flashes, key: band.key, points: Number(difficultyConfig[band.key]?.points) || 0, np: null });
    } else {
      const np = Array.isArray(gradeScale) ? gradeScale[gradeIdx]?.normPoints : null;
      if (np == null) continue;
      out.push({ gradeIdx, count: c, flashes, key: null, points: null, np });
    }
  }
  return out.sort((a, b) => a.gradeIdx - b.gradeIdx);
}
